// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $FullRegattaResultTable,
  $FullRegattaResultTableRace,
  $RegattaEntryResource,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  Row,
} from 'reactstrap';
import {
  AvForm,
} from 'availity-reactstrap-validation';
import {
  InputTypeahead,
  Loader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  getRegattaEntryParams,
} from 'common.helpers/dist/common';
import {
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';

// Components
import InputResult from '../../../form/InputResult';
import RegattaEntry from '../../../common/RegattaEntry';
import RegattaResultTableRaceSelectorSection from '../RegattaResultTableRaceSelectorSection';

// Services
import Services from '../../../../services';

// Types
import type {
  $ActiveRegattaResourceTableRaces,
  $TileResult,
} from '../../../../types';

type $Props = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

type $State = {
  activeRegattaResourceTableRaces: $ActiveRegattaResourceTableRaces;
  blankResults: Array<$TileResult>;
  data: Array<$FullRegattaResultTable>;
  filledResults: Array<$TileResult>;
  loading: boolean;
  mode: 'position' | 'time';
  value: null | string;
};

class RegattaResultSequenceSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      activeRegattaResourceTableRaces: {
      },
      blankResults: [],
      data: [],
      filledResults: [],
      loading: false,
      mode: 'position',
      value: null,
    };
  }

  componentDidMount(): void {
    this.initialGetData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.initialGetData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        const models = ['RegattaResultTable', 'RegattaResultTableEntry', 'RegattaResultTableRace'];

        if (item.id === data.RegattaId && _.includes(
          models,
          data.modelName,
        )) {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        if (RegattaId !== null) {
          const data = await Services.Api.getManyRegattaResultTable({
            RegattaId,
          });

          await this.setStateAsync({
            data,
            loading: false,
          });

          await this.getActiveResults();
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async getActiveResults(): Promise<void> {
    const {
      activeRegattaResourceTableRaces,
      data,
    } = this.state;

    let results: Array<$TileResult> = [];

    _.forEach(
      activeRegattaResourceTableRaces,
      (
        regattaResultTableRaceId: string,
        regattaResultTableId: string,
      ) => {
        const regattaResultTable: $FullRegattaResultTable | undefined = _.find(
          data,
          {
            id: regattaResultTableId,
          },
        );

        if (regattaResultTable) {
          const regattaResultTableRace: $FullRegattaResultTableRace | undefined = _.find(
            regattaResultTable.RegattaResultTableRaces,
            {
              id: regattaResultTableRaceId,
            },
          );

          if (regattaResultTableRace) {
            const raceResults: Array<$TileResult> = regattaResultTableRace
              .RegattaResultTableRaceResults.map((
                regattaResultTableRaceResult,
              ) => {
                const regattaResultTableEntry = _.find(
                  regattaResultTable.RegattaResultTableEntries,
                  {
                    id: regattaResultTableRaceResult.RegattaResultTableEntryId,
                  },
                );

                if (!regattaResultTableEntry) {
                  throw new Error('RegattaResultTableRaceResult RegattaResultTableEntry not found');
                }

                return {
                  ...regattaResultTableRaceResult,
                  RegattaResultTable: regattaResultTable,
                  RegattaResultTableEntry: regattaResultTableEntry,
                  RegattaResultTableRace: regattaResultTableRace,
                };
              });

            results = [...results, ...raceResults];
          }
        }
      },
    );

    const sortedResults: Array<$TileResult> = _.orderBy(
      results,
      ['InputDate'],
      ['asc'],
    );

    const blankResults: Array<$TileResult> = [];
    const filledResults: Array<$TileResult> = [];

    sortedResults.forEach((result: $TileResult) => {
      if (result.Position !== null || result.ET !== 0) {
        filledResults.push(result);
      } else {
        blankResults.push(result);
      }
    });

    await this.setStateAsync({
      blankResults,
      filledResults,
    });
  }

  async initialGetData(): Promise<void> {
    await this.setStateAsync({
      activeRegattaResourceTableRaces: {
      },
      blankResults: [],
      filledResults: [],
      loading: true,
    });

    await this.getData();
  }

  saveResult(result: $TileResult) {
    const {
      mode,
    } = this.state;

    defaultUpsertItem({
      action: async () => {
        const updatedResult = {
          ...result,
          InputDate: new Date(),
        };

        if (mode === 'time') {
          const now: moment.Moment = moment();

          updatedResult.Month = Number(now.format('MM'));
          updatedResult.Day = Number(now.format('DD'));
          updatedResult.Hour = Number(now.format('HH'));
          updatedResult.Minute = Number(now.format('mm'));
          updatedResult.Second = Number(now.format('ss'));
        } else {
          const positions: Array<number> = result
            .RegattaResultTableRace.RegattaResultTableRaceResults.map(({
              Position,
            }) => {
              if (Position && /^-?\d+$/.test(Position)) {
                return Number(Position);
              }

              return 0;
            });

          const highestPosition: number = _.max(positions) || 0;

          updatedResult.Position = String(highestPosition + 1);
        }

        await this.setStateAsync({
          value: '',
        });

        await this.setStateAsync({
          value: null,
        });

        await Services.Api.updateRegattaResultTableRaceResult(updatedResult);
      },
    });
  }

  renderSettingsSection() {
    const {
      t,
    } = this.props;
    const {
      mode,
    } = this.state;

    return (
      <Row>
        <Col>
          <span className={'ml-1'}>
            {t('Save result')}
          </span>
          <label
            className={'checkbox c-checkbox c-checkbox-rounded m-1 ml-3'}
            htmlFor={'positionSwitch'}
          >
            <Input
              checked={mode === 'position'}
              id={'positionSwitch'}
              onClick={() => {
                this.setState({
                  mode: 'position',
                });
              }}
              type={'checkbox'}
            />
            <span className={'fa fa-check'} />
            {t('position')}
          </label>
          <label
            className={'checkbox c-checkbox c-checkbox-rounded m-1 ml-3'}
            htmlFor={'timeSwitch'}
          >
            <Input
              checked={mode === 'time'}
              id={'timeSwitch'}
              onClick={() => {
                this.setState({
                  mode: 'time',
                });
              }}
              type={'checkbox'}
            />
            <span className={'fa fa-check'} />
            {t('time')}
          </label>
        </Col>
      </Row>
    );
  }

  renderListSection() {
    const {
      item,
      t,
    } = this.props;
    const {
      filledResults,
    } = this.state;

    return filledResults.map((result: $TileResult) => (
      <Row
        className={'mb-1'}
        key={result.id}
        title={t('Result')}
      >
        <Col
          md={2}
          sm={3}
          xs={4}
        >
          <RegattaEntry
            bowNoEnabled={item.BowNoEnabled}
            countryEnabled={item.CountryEnabled}
            item={result.RegattaResultTableEntry.RegattaEntry}
            shorten
          />
        </Col>
        <Col
          md={4}
          sm={6}
          xs={8}
        >
          <InputResult
            item={result}
            onChange={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$TileResult>({
                  ...result,
                  ...values,
                });

                await Services.Api.updateRegattaResultTableRaceResult(params);
              },
              quiet: true,
            })}
            raceType={result.RegattaResultTableRace.Type}
          />
        </Col>
      </Row>
    ));
  }

  renderAddSection() {
    const {
      item,
      t,
    } = this.props;
    const {
      blankResults,
      value,
    } = this.state;

    const getOptionLabel = (regattaEntry: $RegattaEntryResource): string => {
      const {
        fullString,
      } = getRegattaEntryParams({
        bowNoEnabled: item.BowNoEnabled,
        countryEnabled: item.CountryEnabled,
        regattaEntry,
      });

      let output = fullString;

      if (regattaEntry.Name === null) {
        const skipperName: string = _.get(
          regattaEntry,
          'RegattaEntryCrewmembers[0].Name',
        ) || '';

        output += ` ${skipperName}`;
      }

      return output;
    };

    return (blankResults.length !== 0 ? (
      <AvForm
        onValidSubmit={async (event: Event, v: {
          entry: string;
        }) => {
          const foundResult: $TileResult | undefined = _.find(
            blankResults,
            (
              result: $TileResult,
            ) => getOptionLabel(result.RegattaResultTableEntry.RegattaEntry) === v.entry,
          );

          if (foundResult) {
            await this.saveResult(foundResult);
          }
        }}
      >
        <Row>
          <Col
            md={4}
            sm={6}
            xs={8}
          >
            <InputTypeahead
              getOptions={async (inputString: string) => {
                const foundItems: Array<$TileResult> = blankResults.filter((
                  result: $TileResult,
                ) => {
                  const searchString = getOptionLabel(result.RegattaResultTableEntry.RegattaEntry);
                  const locSearchString = searchString.toLowerCase();
                  const lowInputString: string = inputString.toLowerCase();

                  return locSearchString.includes(lowInputString);
                });

                return foundItems.map((result: $TileResult) => ({
                  ...result,
                  Name: getOptionLabel(result.RegattaResultTableEntry.RegattaEntry),
                }));
              }}
              minLength={1}
              name={'entry'}
              placeholder={t('Entry')}
              required
              value={value}
            />
          </Col>
          <Col
            className={'text-right'}
            md={2}
            sm={3}
            xs={4}
          >
            <Button
              color={'info'}
              id={'add-button'}
              size={'sm'}
            >
              {t('Add')}
            </Button>
          </Col>
        </Row>
      </AvForm>
    ) : null);
  }

  renderContent() {
    const {
      t,
    } = this.props;
    const {
      blankResults,
      filledResults,
    } = this.state;

    return (blankResults.length !== 0 || filledResults.length !== 0) ? (
      <Card className={'p-1'}>
        {this.renderSettingsSection()}
        <hr className={'mt-0'} />
        {this.renderListSection()}
        {this.renderAddSection()}
      </Card>
    ) : (
      <Card>
        <div className={'text-muted text-center p-3'}>
          {t('Add result table entries')}
        </div>
      </Card>
    );
  }

  render() {
    const {
      id,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    return (
      <div id={id}>
        <Loader
          loading={loading === true}
          overlay
        >
          <RegattaResultTableRaceSelectorSection
            data={data}
            onChange={async (
              activeRegattaResourceTableRaces: $ActiveRegattaResourceTableRaces,
            ) => {
              await this.setStateAsync({
                activeRegattaResourceTableRaces,
              });

              await this.getActiveResults();
            }}
          />
          {this.renderContent()}
        </Loader>
      </div>
    );
  }
}

export default RegattaResultSequenceSectionView;
